/**
 * Справочник норм расхода материалов: логика формы без React — область правки,
 * смена договора во время правки и тело запроса.
 *
 * Замечание F02 экспертизы 03.11.2026: форма помнила только `editingId`.
 * Начал правку нормы договора A, указал договор B, сохранил — уходил PATCH
 * нормы A; начал правку общего класса и выбрал договор — менялся общий класс
 * для всех договоров. Теперь правка помнит область правила, область видна над
 * формой, а смена договора сбрасывает форму (с подтверждением, если в ней
 * есть несохранённые изменения).
 *
 * Тело правки. Форма не показывает `unit_factor` и `norm_per_unit`, а сервер
 * принимал правило целиком и подставлял умолчания вместо непереданного, так что
 * правка процента обнуляла их и меняла источник нормы на «ручной ввод». Теперь
 * в тело идут прежние значения скрытых полей, а источник становится `manual`,
 * только если изменено значение нормы — процент потерь (решение по умолчанию,
 * его подтверждает сметно-ценовой отдел).
 */
import type { ContractMaterialNorm } from '@shared/api';

/** Поля формы — строками, как в полях ввода. */
export interface NormForm {
  material_class: string;
  match_kind: string;
  match_pattern: string;
  unit: string;
  waste_pct: string;
  supply_scope: string;
  priority: string;
  comment: string;
}

export const EMPTY_NORM_FORM: NormForm = {
  material_class: '',
  match_kind: 'substring',
  match_pattern: '',
  unit: '',
  waste_pct: '',
  supply_scope: '',
  priority: '100',
  comment: '',
};

/** Правило, которое сейчас правится, и его область: договор или общий класс. */
export interface NormEditTarget {
  normId: string;
  /** `null` — общий класс для всех договоров. */
  contractId: string | null;
  original: ContractMaterialNorm;
}

/** Состояние редактора: выбранный договор (пусто — общие классы), форма, правка. */
export interface NormEditorState {
  contractId: string;
  form: NormForm;
  editing: NormEditTarget | null;
}

/** Тело создания и правки правила (`/material-norms`). Псевдоним типа, а не
 *  interface: мутации принимают `Record<string, unknown>`. */
export type NormBody = {
  material_class: string;
  match_kind: string;
  match_pattern: string;
  unit: string | null;
  waste_factor: number;
  unit_factor?: number | null;
  norm_per_unit?: number | null;
  supply_scope: string | null;
  priority: number;
  source: string;
  comment: string | null;
};

export type NormSubmitPlan =
  | { kind: 'error'; message: string }
  | { kind: 'create'; contractId: string | null; body: NormBody }
  | { kind: 'update'; normId: string; body: NormBody };

/** Коэффициент 1.033 в поле ввода — это 3,3 % потерь. */
export function pctFromFactor(factor: number): string {
  return ((factor - 1) * 100).toFixed(2).replace(/\.?0+$/, '');
}

export function factorFromPct(pct: string): number | null {
  const text = pct.trim().replace(',', '.');
  if (!text) return null;
  const v = Number(text);
  if (!Number.isFinite(v) || v < 0) return null;
  return 1 + v / 100;
}

/** Процент как число для сравнения «изменён ли» (3,30 и 3.3 — одно и то же). */
function pctValue(pct: string): number | null {
  const text = pct.trim().replace(',', '.');
  if (!text) return null;
  const v = Number(text);
  return Number.isFinite(v) ? v : null;
}

export function formFromNorm(n: ContractMaterialNorm): NormForm {
  return {
    material_class: n.material_class,
    match_kind: n.match_kind,
    match_pattern: n.match_pattern,
    unit: n.unit ?? '',
    waste_pct: pctFromFactor(n.waste_factor),
    supply_scope: n.supply_scope ?? '',
    priority: String(n.priority),
    comment: n.comment ?? '',
  };
}

export function startEdit(state: NormEditorState, n: ContractMaterialNorm): NormEditorState {
  return {
    ...state,
    form: formFromNorm(n),
    editing: { normId: n.id, contractId: n.contract_id ?? null, original: n },
  };
}

export function cancelEdit(state: NormEditorState): NormEditorState {
  return { ...state, form: { ...EMPTY_NORM_FORM }, editing: null };
}

function sameForm(a: NormForm, b: NormForm): boolean {
  return (Object.keys(a) as Array<keyof NormForm>).every((k) =>
    k === 'waste_pct' ? pctValue(a[k]) === pctValue(b[k]) : a[k].trim() === b[k].trim(),
  );
}

/** Есть ли в форме несохранённые изменения. */
export function isFormDirty(state: NormEditorState): boolean {
  const base = state.editing ? formFromNorm(state.editing.original) : EMPTY_NORM_FORM;
  return !sameForm(state.form, base);
}

/**
 * Смена договора. Правка или заполненная форма сбрасываются: правило
 * принадлежит своей области, и сохранять его «в другой договор» нельзя. Если в
 * форме есть несохранённые изменения, сначала спрашиваем (`confirm`); отказ —
 * договор не меняется.
 */
export function switchContract(
  state: NormEditorState,
  nextContractId: string,
  confirm: () => boolean,
): NormEditorState {
  if (nextContractId.trim() === state.contractId.trim()) {
    return { ...state, contractId: nextContractId };
  }
  const dirty = isFormDirty(state);
  if (!state.editing && !dirty) return { ...state, contractId: nextContractId };
  if (dirty && !confirm()) return state;
  return { contractId: nextContractId, form: { ...EMPTY_NORM_FORM }, editing: null };
}

/** Подпись области над формой: куда уйдёт сохранение. */
export function editScopeText(state: NormEditorState): string {
  const contract = state.contractId.trim();
  if (state.editing) {
    return state.editing.contractId
      ? `Правка правила договора ${state.editing.contractId}.`
      : 'Правка общего класса: изменение затронет все договоры, где нет своего правила.';
  }
  return contract
    ? `Новое правило для договора ${contract}.`
    : 'Новый общий класс — для всех договоров.';
}

function parsePriority(text: string, fallback: number): number | null {
  const t = text.trim();
  if (!t) return fallback;
  const v = Number(t);
  return Number.isInteger(v) ? v : null;
}

/** Что отправить на сервер по кнопке «Сохранить» / «Добавить правило». */
export function planSubmit(state: NormEditorState): NormSubmitPlan {
  const { form, editing } = state;
  const contract = state.contractId.trim();
  if (!form.material_class.trim() || !form.match_pattern.trim()) {
    return { kind: 'error', message: 'Заполните класс материала и что искать в наименовании.' };
  }
  const waste = factorFromPct(form.waste_pct);
  if (waste === null) {
    return { kind: 'error', message: 'Процент потерь должен быть числом от нуля.' };
  }
  const priority = parsePriority(form.priority, editing ? editing.original.priority : 100);
  if (priority === null) {
    return {
      kind: 'error',
      message: 'Приоритет — целое число: чем оно меньше, тем раньше применяется правило.',
    };
  }
  const common = {
    material_class: form.material_class.trim(),
    match_kind: form.match_kind,
    match_pattern: form.match_pattern.trim(),
    unit: form.unit.trim() || null,
    supply_scope: form.supply_scope || null,
    priority,
    comment: form.comment.trim() || null,
  };
  if (!editing) {
    return {
      kind: 'create',
      contractId: contract || null,
      body: { ...common, waste_factor: waste, source: 'manual' },
    };
  }
  // Правило договора правится только из этого договора: иначе сохранение ушло
  // бы в чужую область (общие классы видны и из договора — их правка разрешена).
  if (editing.contractId && editing.contractId !== contract) {
    return {
      kind: 'error',
      message: 'Правило относится к другому договору. Отмените правку и выберите его договор.',
    };
  }
  const { original } = editing;
  const valueChanged = pctValue(form.waste_pct) !== pctValue(pctFromFactor(original.waste_factor));
  return {
    kind: 'update',
    normId: editing.normId,
    body: {
      ...common,
      // Процент не трогали — сохраняем коэффициент как был, без погрешности
      // пересчёта «процент → коэффициент».
      waste_factor: valueChanged ? waste : original.waste_factor,
      unit_factor: original.unit_factor,
      norm_per_unit: original.norm_per_unit,
      source: valueChanged ? 'manual' : original.source,
    },
  };
}

/** Вопрос перед удалением: общий класс действует для всех договоров. */
export function deleteConfirmText(n: ContractMaterialNorm): string {
  return n.contract_id
    ? `Удалить правило договора «${n.material_class}» (${n.match_pattern})?`
    : `Удалить общий класс «${n.material_class}» (${n.match_pattern})? Он действует для всех ` +
        'договоров: норма пропадёт везде, где у договора нет своего правила.';
}
